import React, { useEffect, useRef, useState } from 'react'
import { CircleCheck, Loader } from 'lucide-react'

interface ResultsCountProps {
    isLoading: boolean
    count: number
    loadingText?: string
    noun?: string
}

/** How long "N new companies found" stays before returning to the total. */
export const NEW_RESULTS_NOTICE_MS = 10_000
const FADE_MS = 250

type Phase = { kind: 'loading' } | { kind: 'new'; added: number } | { kind: 'total' }

/** Fades out the old line and fades in the new one whenever `phaseKey` changes. */
const FadeSwap = ({ phaseKey, children }: { phaseKey: string; children: React.ReactNode }) => {
    const [shown, setShown] = useState({ key: phaseKey, node: children })
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        if (phaseKey === shown.key) {
            setShown({ key: phaseKey, node: children })
            return undefined
        }
        setVisible(false)
        const timer = setTimeout(() => {
            setShown({ key: phaseKey, node: children })
            setVisible(true)
        }, FADE_MS)
        return () => clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- children follow phaseKey
    }, [phaseKey, children])

    return (
        <div
            className={`transition-all ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'}`}
            style={{ transitionDuration: `${FADE_MS}ms` }}
        >
            {shown.node}
        </div>
    )
}

// result count
export default function ResultsCount({
    isLoading,
    count,
    loadingText = 'Researching Companies',
    noun = 'Companies',
}: ResultsCountProps) {
    const [phase, setPhase] = useState<Phase>(isLoading ? { kind: 'loading' } : { kind: 'total' })
    const countAtStart = useRef(count)
    const wasLoading = useRef(isLoading)

    useEffect(() => {
        if (isLoading && !wasLoading.current) {
            countAtStart.current = count
            setPhase({ kind: 'loading' })
        } else if (!isLoading && wasLoading.current) {
            const added = count - countAtStart.current
            setPhase(added > 0 ? { kind: 'new', added } : { kind: 'total' })
        }
        wasLoading.current = isLoading
    }, [isLoading, count])

    useEffect(() => {
        if (phase.kind !== 'new') return undefined
        const timer = setTimeout(() => setPhase({ kind: 'total' }), NEW_RESULTS_NOTICE_MS)
        return () => clearTimeout(timer)
    }, [phase])

    let line: React.ReactNode
    if (phase.kind === 'loading') {
        line = (
            <div className='flex items-center gap-3 text-sm font-normal font-sans text-cyan-600 dark:text-cyan-400'>
                <Loader size={14} className='animate-spin' />
                <span className='text-gray-500'>{loadingText}</span>
            </div>
        )
    } else if (phase.kind === 'new') {
        line = (
            <div className='flex items-center gap-2 text-sm font-sans text-success-500 dark:text-success-500' role='status'>
                <CircleCheck size={16} className='text-success-500 shrink-0' />
                <span>
                    <span className='font-medium text-success-500 dark:text-success-500'>{phase.added}</span> new{' '}
                    {phase.added === 1 ? noun.replace(/ies$/, 'y').replace(/s$/, '').toLowerCase() : noun.toLowerCase()} found
                </span>
            </div>
        )
    } else {
        line = (
            <div className='text-sm text-gray-400 dark:text-gray-400 font-sans'>
                Found{' '}
                <span className='font-medium text-gray-900 dark:text-white'>
                    {count > 0 ? String(count).padStart(2, '0') : '0'} {noun}
                </span>
            </div>
        )
    }

    return <FadeSwap phaseKey={phase.kind === 'new' ? `new-${phase.added}` : phase.kind}>{line}</FadeSwap>
}
